import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import {
  Plus,
  ExternalLink,
  MoreHorizontal,
  MapPin,
  Monitor,
  Eye,
  PauseCircle,
  XCircle,
  PlayCircle,
  CheckCircle,
  Trash2,
} from 'lucide-react';
import { useClients, useUpdateSubscriptionStatus, useDeleteOrganization } from '@/hooks/useClients';
import { formatCurrency, formatDate, getClientPOSUrl, calculateMRR, cn } from '@/lib/utils';
import { AddClientModal } from '@/components/AddClientModal';
import { DataTable, DataTableColumnHeader } from '@/components/ui/data-table';
import type { ClientWithSubscription } from '@/lib/database.types';

// Map subscription_info status to display status
function getDisplayStatus(client: ClientWithSubscription): string {
  const subStatus = client.subscription?.status;
  if (subStatus === 'trialing') return 'trial';
  if (subStatus === 'active') return 'active';
  if (subStatus === 'cancelled') return 'cancelled';
  if (subStatus === 'past_due') return 'past_due';
  if (subStatus === 'unpaid') return 'suspended';
  if (client.subscription_status === 'suspended') return 'suspended';
  return subStatus || 'trial';
}

export default function Clients() {
  const navigate = useNavigate();
  const { data: clients, isLoading } = useClients();
  const updateStatus = useUpdateSubscriptionStatus();
  const deleteOrg = useDeleteOrganization();
  const [showAddModal, setShowAddModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; right: number }>({ top: 0, right: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const openMenuFor = (clientId: string, button: HTMLButtonElement) => {
    if (openDropdownId === clientId) {
      setOpenDropdownId(null);
      return;
    }
    const rect = button.getBoundingClientRect();
    triggerRef.current = button;
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setOpenDropdownId(clientId);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpenDropdownId(null);
    }
    function handleDismiss() {
      setOpenDropdownId(null);
    }
    document.addEventListener('mousedown', handleClickOutside);
    // Close on scroll/resize since the portal menu is fixed-positioned
    window.addEventListener('scroll', handleDismiss, true);
    window.addEventListener('resize', handleDismiss);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleDismiss, true);
      window.removeEventListener('resize', handleDismiss);
    };
  }, []);

  const handleStatusChange = async (orgId: string, newStatus: 'trialing' | 'active' | 'cancelled' | 'unpaid') => {
    try {
      await updateStatus.mutateAsync({ organizationId: orgId, status: newStatus });
      setOpenDropdownId(null);
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      trial: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      suspended: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      past_due: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    };
    return (
      <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || styles.cancelled)}>
        {status}
      </span>
    );
  };

  const columns: ColumnDef<ClientWithSubscription>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Client" />
      ),
      cell: ({ row }) => {
        const client = row.original;
        return (
          <Link to={`/clients/${client.slug}`} className="block">
            <p className="font-medium text-gray-900 dark:text-white hover:text-blue-600">
              {client.name}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {client.slug}.blazepos.app
            </p>
          </Link>
        );
      },
    },
    {
      id: 'plan',
      header: 'Plan',
      cell: ({ row }) => {
        const plan = row.original.subscription?.plan;
        return (
          <p className="text-sm text-gray-900 dark:text-white">
            {plan?.name || row.original.subscription?.plan_name || 'No plan'}
          </p>
        );
      },
    },
    {
      id: 'licenses',
      header: 'Licenses',
      cell: ({ row }) => {
        const subscription = row.original.subscription;
        return (
          <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
            <span className="flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {subscription?.licensed_locations || 0}
            </span>
            <span className="flex items-center gap-1">
              <Monitor className="h-3.5 w-3.5" />
              {subscription?.licensed_registers || 0}
            </span>
          </div>
        );
      },
    },
    {
      id: 'mrr',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="MRR" />
      ),
      accessorFn: (row) => {
        const subscription = row.subscription;
        const plan = subscription?.plan;
        return plan && subscription ? calculateMRR(plan, subscription) : 0;
      },
      cell: ({ row }) => {
        const subscription = row.original.subscription;
        const plan = subscription?.plan;
        const mrr = plan && subscription ? calculateMRR(plan, subscription) : 0;
        return (
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {formatCurrency(mrr)}
          </p>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => getDisplayStatus(row),
      cell: ({ row }) => getStatusBadge(getDisplayStatus(row.original)),
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Created" />
      ),
      cell: ({ row }) => (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {formatDate(row.getValue('created_at'))}
        </p>
      ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: ({ row }) => {
        const client = row.original;
        return (
          <div className="flex items-center justify-end gap-2">
            <a
              href={getClientPOSUrl(client.slug)}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title="Open POS"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
            <button
              onClick={(e) => openMenuFor(client.id, e.currentTarget)}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Client actions menu"
              title="Actions"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </div>
        );
      },
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Clients</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage all BlazePOS client organizations
          </p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Client
        </button>
      </div>

      <AddClientModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />

      <DataTable
        columns={columns}
        data={clients || []}
        searchKey="name"
        searchPlaceholder="Search clients..."
      />

      <ClientActionsMenu
        client={clients?.find((c) => c.id === openDropdownId) ?? null}
        pos={menuPos}
        menuRef={menuRef}
        onClose={() => setOpenDropdownId(null)}
        onViewDetails={(slug) => {
          navigate(`/clients/${slug}`);
          setOpenDropdownId(null);
        }}
        onStatusChange={handleStatusChange}
        statusPending={updateStatus.isPending}
        onDelete={async (client) => {
          if (!confirm(`Permanently delete "${client.name}" and all their data? This cannot be undone.`)) return;
          if (!confirm(`Final confirmation: Delete ${client.name}, all their locations, orders, products, and user accounts?`)) return;
          setOpenDropdownId(null);
          try {
            await deleteOrg.mutateAsync({ organizationId: client.id });
            alert(`${client.name} has been permanently deleted.`);
          } catch (err) {
            alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
          }
        }}
        deletePending={deleteOrg.isPending}
      />
    </div>
  );
}

interface ClientActionsMenuProps {
  client: ClientWithSubscription | null;
  pos: { top: number; right: number };
  menuRef: React.RefObject<HTMLDivElement>;
  onClose: () => void;
  onViewDetails: (slug: string) => void;
  onStatusChange: (orgId: string, status: 'trialing' | 'active' | 'cancelled' | 'unpaid') => void;
  statusPending: boolean;
  onDelete: (client: ClientWithSubscription) => void;
  deletePending: boolean;
}

function ClientActionsMenu({
  client,
  pos,
  menuRef,
  onViewDetails,
  onStatusChange,
  statusPending,
  onDelete,
  deletePending,
}: ClientActionsMenuProps) {
  if (!client) return null;
  const status = getDisplayStatus(client);

  return createPortal(
    <div
      ref={menuRef}
      style={{ position: 'fixed', top: pos.top, right: pos.right }}
      className="w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-[100]"
    >
      <button
        onClick={() => onViewDetails(client.slug)}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
      >
        <Eye className="h-4 w-4" />
        View Details
      </button>

      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

      {status !== 'active' && (
        <button
          onClick={() => onStatusChange(client.id, 'active')}
          disabled={statusPending}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <CheckCircle className="h-4 w-4" />
          Mark as Active
        </button>
      )}

      {status !== 'trial' && status !== 'active' && (
        <button
          onClick={() => onStatusChange(client.id, 'trialing')}
          disabled={statusPending}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <PlayCircle className="h-4 w-4" />
          Start Trial
        </button>
      )}

      {status !== 'suspended' && (
        <button
          onClick={() => onStatusChange(client.id, 'unpaid')}
          disabled={statusPending}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-yellow-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <PauseCircle className="h-4 w-4" />
          Suspend Client
        </button>
      )}

      {status !== 'cancelled' && (
        <button
          onClick={() => onStatusChange(client.id, 'cancelled')}
          disabled={statusPending}
          className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <XCircle className="h-4 w-4" />
          Cancel Client
        </button>
      )}

      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />

      <button
        onClick={() => onDelete(client)}
        disabled={deletePending}
        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-700 font-medium hover:bg-red-50 dark:hover:bg-red-900/20"
      >
        <Trash2 className="h-4 w-4" />
        Delete Permanently
      </button>
    </div>,
    document.body
  );
}
